import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MsalProvider } from '@azure/msal-react';
import type { IPublicClientApplication } from '@azure/msal-browser';
import { App } from './App';
import { AuthProvider } from './auth/AuthProvider';
import { authMode, createMsalInstance } from './auth/config';
import { decodeTarget } from './auth/deep-link';
import { setRestoreTarget } from './auth/redirect-state';
import { ChunkErrorBoundary } from './components/ChunkErrorBoundary';
import { ME_QUERY_KEY } from './auth/useMe';
import { applyTheme, readThemeChoice } from './components/ThemeToggle';
import './index.css';

/**
 * Stamp the theme class before the first paint.
 *
 * Doing it inside a component means one frame of the wrong theme on every
 * load, which on a dark-mode machine is a white flash.
 */
applyTheme(readThemeChoice());

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

/**
 * Handle the Entra redirect BEFORE rendering.
 *
 * `handleRedirectPromise` is called explicitly rather than left to
 * `MsalProvider`, because the intended path is round-tripped in MSAL `state`
 * and we need it in hand before the router mounts. `navigateToLoginRequestUrl`
 * is off for the same reason: we do the navigation, so the query string and
 * hash survive (§8e, Phase 1 step 7).
 */
async function bootstrap(): Promise<void> {
  let msal: IPublicClientApplication | undefined;

  if (authMode === 'entra') {
    const instance = createMsalInstance();
    await instance.initialize();

    const result = await instance.handleRedirectPromise();
    if (result) {
      instance.setActiveAccount(result.account);
      setRestoreTarget(decodeTarget(result.state));
    } else if (!instance.getActiveAccount()) {
      const [account] = instance.getAllAccounts();
      if (account) instance.setActiveAccount(account);
    }

    msal = instance;
  }

  const tree = (
    <StrictMode>
      <ChunkErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <AuthProvider
              {...(msal ? { msal } : {})}
              onIdentityChanged={() => {
                // Switching dev users changes who the server thinks we are, so
                // every cached answer is now about the wrong person.
                void queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
                void queryClient.invalidateQueries();
              }}
            >
              <App />
            </AuthProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </ChunkErrorBoundary>
    </StrictMode>
  );

  const root = createRoot(document.getElementById('root')!);
  root.render(msal ? <MsalProvider instance={msal}>{tree}</MsalProvider> : tree);
}

void bootstrap();
